/**
 * El equipo Apple, elegido con botones: qué es, cuál, y lo que lo distingue.
 *
 * Cada pregunta aparece cuando la anterior está contestada, como en el
 * mostrador: no se pregunta la pantalla de algo que todavía no se sabe si es
 * una Mac o unos AirPods. Lo que la línea tiene de una sola forma (el Ultra
 * solo viene con celular, el iMac en 24") no se pregunta: se completa solo.
 */
import { etiquetaCapacidad } from "@/components/iphones/logica";
import { Campo, GrupoCampo } from "@/components/ui/campo";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";

import {
  DISCO_MAC,
  FAMILIAS,
  LINEAS,
  MEMORIA_IPAD,
  ajustar,
  lineaDe,
  ramDeLinea,
  type EquipoApple as Equipo,
  type FamiliaApple,
} from "./equiposApple";

export interface EquipoAppleProps {
  equipo: Equipo;
  onEquipo: (equipo: Equipo) => void;
  /** «Otro equipo»: el nombre escrito a mano, como antes. */
  nombreLibre: string;
  onNombreLibre: (nombre: string) => void;
  error?: string;
  placeholder?: string;
}

function chips(valores: ReadonlyArray<string>, etiqueta: (v: string) => string = (v) => v) {
  return valores.map((v) => ({ valor: v, etiqueta: etiqueta(v) }));
}

export function EquipoApple({
  equipo,
  onEquipo,
  nombreLibre,
  onNombreLibre,
  error,
  placeholder,
}: EquipoAppleProps): JSX.Element {
  const cambiar = (cambios: Partial<Equipo>): void => onEquipo(ajustar(equipo, cambios));
  const linea = lineaDe(equipo);
  const familia = equipo.familia;
  const esApple = familia !== "" && familia !== "OTRO";

  // Hasta dónde se contestó: cada grupo aparece cuando el anterior tiene valor.
  const conChip = !linea?.chips || equipo.chip !== "";
  const pantallas = linea?.pantallas ?? [];
  const conPantalla = conChip && (pantallas.length <= 1 || equipo.pantalla !== "");
  const conRam = conPantalla && (familia !== "MAC" || equipo.ramGb !== null);
  const conMemoria =
    conRam && (!(familia === "MAC" || familia === "IPAD") || equipo.memoriaGb !== null);
  const conexiones = linea?.conexiones ?? [];

  return (
    <div className="space-y-4">
      <GrupoCampo label="¿Qué equipo es?" requerido error={familia === "" ? error : undefined}>
        <Segmented
          apariencia="chips"
          tamano="md"
          etiqueta="¿Qué equipo es?"
          opciones={FAMILIAS}
          valor={familia}
          onChange={(v) => cambiar({ familia: v as FamiliaApple })}
        />
      </GrupoCampo>

      {familia === "OTRO" ? (
        <Campo
          label="¿Qué es?"
          ayuda="Como se lo nombra al cliente: marca, modelo y lo que lo distingue."
          error={error}
          requerido
        >
          <Input
            autoComplete="off"
            placeholder={placeholder}
            value={nombreLibre}
            onChange={(e) => onNombreLibre(e.target.value)}
          />
        </Campo>
      ) : null}

      {esApple ? (
        <GrupoCampo label="¿Cuál?" requerido>
          <Segmented
            apariencia="chips"
            tamano="md"
            etiqueta="¿Cuál?"
            opciones={LINEAS[familia].map((l) => ({ valor: l.valor, etiqueta: l.etiqueta }))}
            valor={equipo.linea}
            onChange={(v) => cambiar({ linea: v })}
          />
        </GrupoCampo>
      ) : null}

      {linea?.chips ? (
        <GrupoCampo label="Chip" requerido>
          <Segmented
            apariencia="chips"
            tamano="md"
            etiqueta="Chip"
            opciones={chips(linea.chips, (c) => c.replace(" PRO", " Pro").replace(" MAX", " Max"))}
            valor={equipo.chip}
            onChange={(v) => cambiar({ chip: v })}
          />
        </GrupoCampo>
      ) : null}

      {linea && conChip && pantallas.length > 1 ? (
        <GrupoCampo label={familia === "WATCH" ? "Tamaño" : "Pantalla"} requerido>
          <Segmented
            apariencia="chips"
            tamano="md"
            etiqueta={familia === "WATCH" ? "Tamaño" : "Pantalla"}
            opciones={chips(pantallas, (p) => (familia === "WATCH" ? `${p} mm` : `${p}"`))}
            valor={equipo.pantalla}
            onChange={(v) => cambiar({ pantalla: v })}
          />
        </GrupoCampo>
      ) : null}

      {/* RAM y disco uno debajo del otro, cada uno en su renglón: lado a lado
          la RAM (ocho opciones) se partía en tres filas y el disco en dos, y
          las columnas terminaban a distinta altura. */}
      {familia === "MAC" && linea && conPantalla ? (
        <div className="space-y-4">
          <GrupoCampo label="Memoria RAM" requerido>
            <Segmented
              apariencia="chips"
              tamano="md"
              etiqueta="Memoria RAM"
              // La de esa línea: la Air no viene con 64 GB.
              opciones={ramDeLinea(equipo).map((gb) => ({
                valor: String(gb),
                etiqueta: `${gb} GB`,
              }))}
              valor={equipo.ramGb === null ? "" : String(equipo.ramGb)}
              onChange={(v) => cambiar({ ramGb: Number(v) })}
            />
          </GrupoCampo>
          {conRam ? (
            <GrupoCampo label="Disco" requerido>
              <Segmented
                apariencia="chips"
                tamano="md"
                etiqueta="Disco"
                opciones={DISCO_MAC.map((gb) => ({
                  valor: String(gb),
                  etiqueta: etiquetaCapacidad(gb),
                }))}
                valor={equipo.memoriaGb === null ? "" : String(equipo.memoriaGb)}
                onChange={(v) => cambiar({ memoriaGb: Number(v) })}
              />
            </GrupoCampo>
          ) : null}
        </div>
      ) : null}

      {familia === "IPAD" && linea && conPantalla ? (
        <GrupoCampo label="Memoria" requerido>
          <Segmented
            apariencia="chips"
            tamano="md"
            etiqueta="Memoria"
            opciones={MEMORIA_IPAD.map((gb) => ({
              valor: String(gb),
              etiqueta: etiquetaCapacidad(gb),
            }))}
            valor={equipo.memoriaGb === null ? "" : String(equipo.memoriaGb)}
            onChange={(v) => cambiar({ memoriaGb: Number(v) })}
          />
        </GrupoCampo>
      ) : null}

      {linea && conMemoria && conexiones.length > 1 ? (
        <GrupoCampo label="Conexión" requerido>
          <Segmented
            apariencia="chips"
            tamano="md"
            etiqueta="Conexión"
            opciones={chips(conexiones, (c) =>
              c === "WIFI"
                ? "WiFi"
                : c === "WIFI + CELLULAR"
                  ? "WiFi + Cellular"
                  : c === "GPS"
                    ? "GPS"
                    : "GPS + Cellular",
            )}
            valor={equipo.conexion}
            onChange={(v) => cambiar({ conexion: v })}
          />
        </GrupoCampo>
      ) : null}

      {esApple && error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
